'use client';

import { Button, ButtonParams } from '@components/button';
import { CardWithGradient } from '@components/card';
import { CTAButtons } from '@components/ctaButtons';
import { ResponsiveSection } from '@components/responsiveSection';
import { Sanitizer } from '@components/sanitizer';
import { Swiper, SwiperSlide } from 'swiper/react';

import { HowItWorksStep } from './types';

interface Props {
  steps: HowItWorksStep[];
  mobileCardHeight?: string | number;
  desktopCardHeight?: string | number;
  gridCols?: number;
}

export const HowItWorks = ({
  steps,
  mobileCardHeight = '260px',
  desktopCardHeight = '260px',
  gridCols = 3,
}: Props) => {
  const hasSwipe = steps.length > 3;

  return (
    <ResponsiveSection
      mobile={
        <>
          {hasSwipe && (
            <div className="flex" style={{ height: mobileCardHeight }}>
              <Swiper
                spaceBetween={16}
                slidesPerView="auto"
                className="ml-[-8px]! pr-14! pl-6!"
              >
                {steps?.map(
                  ({
                    id,
                    stepNumber,
                    description,
                    pcPlacement,
                    mobilePlacement,
                    buttonLabel,
                    buttonHref,
                    button,
                  }) => (
                    <SwiperSlide key={id}>
                      <CardWithGradient
                        rounded="16"
                        pcPlacement={pcPlacement}
                        mobilePlacement={mobilePlacement}
                      >
                        <div className="flex h-full w-full flex-col justify-between p-6 text-[18px] leading-[28px] max-sm:p-5">
                          <Sanitizer as="span" htmlContent={description} />
                          <div className="flex items-center justify-between">
                            <span className="text-text-base-secondary">
                              {stepNumber}
                            </span>
                            <InnerButton
                              button={button}
                              buttonHref={buttonHref}
                              buttonLabel={buttonLabel}
                            />
                          </div>
                        </div>
                      </CardWithGradient>
                    </SwiperSlide>
                  ),
                )}
              </Swiper>
            </div>
          )}

          {!hasSwipe && (
            <div className="flex flex-col gap-4 px-4">
              {/** Версия для мобильных */}
              {steps?.map(
                ({
                  id,
                  stepNumber,
                  description,
                  pcPlacement,
                  mobilePlacement,
                  buttonLabel,
                  buttonHref,
                  button,
                }) => (
                  <div
                    key={id}
                    className="flex"
                    style={{ height: mobileCardHeight }}
                  >
                    <CardWithGradient
                      rounded="16"
                      pcPlacement={pcPlacement}
                      mobilePlacement={mobilePlacement}
                      className="border border-(--custom-surface-contrast-low)"
                    >
                      <div className="flex h-full w-full flex-col justify-between p-6 text-[18px] leading-[28px] max-sm:p-5">
                        <Sanitizer as="span" htmlContent={description} />
                        <div className="flex items-center justify-between">
                          <span className="text-text-base-secondary">
                            {stepNumber}
                          </span>
                          <InnerButton
                            button={button}
                            buttonHref={buttonHref}
                            buttonLabel={buttonLabel}
                          />
                        </div>
                      </div>
                    </CardWithGradient>
                  </div>
                ),
              )}
            </div>
          )}
        </>
      }
      web={
        <div
          className="grid gap-8 px-4"
          style={{
            gridTemplateRows: desktopCardHeight,
            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
          }}
        >
          {steps.map(
            ({
              id,
              stepNumber,
              description,
              buttonHref,
              buttonLabel,
              button,
              pcPlacement,
              mobilePlacement,
              colSpan,
            }) => (
              <div
                key={id}
                style={colSpan ? { gridColumn: `span ${colSpan}` } : undefined}
              >
                <CardWithGradient
                  rounded="16"
                  pcPlacement={pcPlacement}
                  mobilePlacement={mobilePlacement}
                  className="border border-(--custom-surface-contrast-low)"
                >
                  <div
                    style={{ minHeight: desktopCardHeight }}
                    className="flex h-full w-full flex-col justify-between p-6 text-[18px] leading-[28px]"
                  >
                    <Sanitizer as="span" htmlContent={description} />
                    <div className="flex items-center justify-between">
                      <span className="text-text-base-secondary">
                        {stepNumber}
                      </span>
                      <InnerButton
                        button={button}
                        buttonHref={buttonHref}
                        buttonLabel={buttonLabel}
                      />
                    </div>
                  </div>
                </CardWithGradient>
              </div>
            ),
          )}
        </div>
      }
    />
  );
};

const InnerButton = ({
  buttonHref,
  buttonLabel,
  button,
}: {
  buttonLabel?: string;
  buttonHref?: string;
  button?: ButtonParams;
}) => {
  if (button?.label) {
    return <CTAButtons secondaryButton={button} />;
  }

  if (buttonHref) {
    return (
      <Button
        href={buttonHref}
        target="_blank"
        variant="secondary"
        className="flex items-center justify-center px-6 text-[16px] leading-[24px]"
      >
        {buttonLabel}
      </Button>
    );
  }

  return null;
};
